<template>
  <BaseCard
    :result="{
      orgName: searchResult.secondary_organization.name,
      orgLink: searchResult.link,
      tagName: $t(`search.athletics.tagName`),
      tagLink: `${searchResult.link}/athletics`,
      id: searchResult.id,
      type: searchResult.type,
      result_position: resultPosition,
      display_text: searchResult.title,
    }"
    class="athletics-card result-card"
    @result-clicked="handleResultClick"
  >
    <template #preview>
      <div
        v-if="searchResult?.image_url"
        class="preview-image"
        data-testid="athletics-card-preview-image"
      >
        <img
          :src="searchResult.image_url"
          :alt="searchResult.alt_text"
        />
      </div>
      <div
        v-else
        class="preview-fallback"
        data-testid="athletics-card-preview-image-fallback"
      >
        <div class="day">{{ shortDay }}</div>
        <div class="month">{{ shortMonth }}</div>
      </div>
    </template>

    <template #result-content>
      <a
        v-if="searchResult.title"
        :href="url"
        class="title"
        data-testid="athletics-card-title"
        @click.stop="handleResultClick"
      >
        {{ searchResult.title }}
      </a>
      <div
        v-else
        class="title"
        data-testid="athletics-card-title-fallback"
      >
        {{
          $t('search.athletics.vs', {
            homeTeam: searchResult.home_team || 'Home',
            awayTeam: searchResult.away_team || 'Away',
          })
        }}
      </div>

      <div
        class="meta-info"
        :class="{ 'is-mobile-view': isMobileView }"
      >
        <div
          class="date"
          data-testid="athletics-card-date"
        >
          <Icon name="lucide:calendar" />
          {{ searchResult.formatted_date }}
        </div>
        <a
          v-if="searchResult.location"
          :href="`http://maps.google.com/?q=${encodeURIComponent(searchResult.location)}`"
          class="location"
          target="_blank"
          rel="noopener noreferrer"
          data-testid="athletics-card-location"
          @click.stop
        >
          <Icon
            name="lucide:map-pin"
            size="14"
          />
          {{ searchResult.location }}
        </a>
      </div>

      <div class="card-buttons">
        <button
          ref="add-to-calendar-button-athletics"
          class="button add-to-calendar"
          :aria-expanded="showCalendarButtons.toString()"
          :aria-controls="`add-buttons-${searchResult.id}`"
          data-testid="athletics-card-add-to-calendar-button"
          @click.stop="toggleCalendarButtons"
        >
          <Icon name="lucide:calendar-plus" />
          <span>{{ $t('search.athletics.addToCalendar') }}</span>
        </button>
        <!-- <button class="button register">
            <Icon name="lucide:ticket" />
            {{ $t('search.events.register') }}
          </button> -->
        <div
          v-show="showCalendarButtons"
          :id="`add-buttons-${searchResult.id}`"
          class="calendar-popup"
          data-testid="athletics-card-add-to-calendar-popup"
        >
          <EventListButtons
            :show-calendar-buttons="showCalendarButtons"
            :event-data="searchResult"
            :handle-back-button-click="handleBackButtonClick"
            :handle-blur="handleBlur"
            :color-overrides="colorOverrides"
            use-new-close-icon
          />
        </div>
        <PublishedUpdatedDate :search-result="searchResult" />
      </div>
    </template>
  </BaseCard>
</template>

<script setup>
import BaseCard from './BaseCard.vue';
import { computed, ref, useTemplateRef, nextTick } from 'vue';
import { useScreenBreakpoints } from '@/composables/useBreakpoints';
import { useThemedButtons } from '@/composables/useThemedButtons';
import EventListButtons from '@/components/events/components/EventViews/List/EventListButtons.vue';
import PublishedUpdatedDate from './PublishedUpdatedDate.vue';
import { useCardShortFormattedDate } from '~/composables/useCardShortFormattedDate';

const addToCalendarButton = useTemplateRef('add-to-calendar-button-athletics');

const { isMobileView } = useScreenBreakpoints();

const props = defineProps({
  searchResult: {
    type: Object,
    required: true,
  },
  resultPosition: {
    type: Number,
    required: true,
  },
});

const emit = defineEmits(['result-clicked']);

const url = computed(() => {
  return `${props.searchResult.link}/athletics#${props.searchResult.id}`;
});

const { month: shortMonth, day: shortDay } = useCardShortFormattedDate(props.searchResult.formatted_date);

const showCalendarButtons = ref(false);

function toggleCalendarButtons() {
  showCalendarButtons.value = !showCalendarButtons.value;
  if (showCalendarButtons.value) {
    const calendarOption = useTemplateRef('calendar-option-0');
    nextTick(() => {
      calendarOption[0]?.focus();
    });
  }
}
function handleBackButtonClick() {
  showCalendarButtons.value = false;
  addToCalendarButton.value.focus();
}
function handleBlur() {
  showCalendarButtons.value = false;
  addToCalendarButton.value.focus();
}
const { themedButtons } = useThemedButtons();
const colorOverrides = computed(() => {
  return {
    '--button-border': themedButtons['--tertiary-button-border'],
    '--button-primary-bg': themedButtons['--primary-button-background'],
    '--button-primary-bg-hover': themedButtons['--secondary-button-active'],
    // focus state
    '--button-outline': themedButtons['--primary-button-focus-outline'],
    // close calendar button
    '--button-default-bg': themedButtons['--secondary-button-background'],
    '--button-default-text': themedButtons['--secondary-button-text'],
    '--button-primary-text': themedButtons['--primary-button-text-contrast'],
  };
});

async function handleResultClick() {
  await navigateTo(url.value, { external: true });
  emit('result-clicked', {
    position: props.resultPosition,
    url: url.value,
  });
}
</script>

<style scoped>
.athletics-card {
  --background-color: var(--color-grey-50);
  --text-color: var(--color-grey-950);
  --color-grey-50: #f7f7f7;
  --color-grey-700: #616161;
  --color-grey-950: #2d2d2d;

  .title {
    line-height: 120%;
  }

  .preview-fallback {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    .day {
      font-size: 1.5rem;
      font-weight: bold;
    }

    .month {
      font-size: 1rem;
      color: var(--color-grey-700);
      text-transform: uppercase;
    }
  }
  .meta-info {
    &.is-mobile-view {
      display: flex;
      flex-direction: column;
      gap: 8px;
    }
    .iconify {
      flex-shrink: 0;
    }
  }
  .card-buttons {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    .button {
      padding: 0.5rem 1rem;
      cursor: pointer;
      font-weight: 500;
      transition:
        background 0.2s,
        color 0.2s;
      &.add-to-calendar {
        background-color: var(--secondary-button-background);
        color: var(--secondary-button-text);
        border: var(--secondary-button-border);
        border-radius: var(--secondary-button-border-radius);
        display: flex;
        align-items: center;
        margin-right: 12px;
        .iconify {
          margin-right: 6px;
        }
        &:hover {
          background-color: var(--secondary-button-hover);
        }
        &:focus-visible {
          outline: var(--secondary-button-focus-outline);
          outline-offset: 1px;
          border: var(--secondary-button-hover-border);
        }
      }
      svg {
        margin-right: 6px;
      }
    }
    .calendar-popup {
      position: relative;
      z-index: 10;
      padding: 12px 0 8px 0;
      display: flex;
      flex-direction: column;
      align-items: stretch;
      gap: 4px;
      .button-container.open {
        background: var(--primary-white, #fff);
        position: absolute;
        left: -182px;
        margin-top: 24px;
        min-width: 180px;
        border: 1px solid var(--color-grey-100, #e0e0e0);
        border-radius: 12px;
        z-index: 100;
      }
    }
  }
}
</style>
